import "@fontsource/figtree/400.css";
import "@fontsource/figtree/500.css";
import "@fontsource/figtree/600.css";
import {
  ArrowUpRight,
  ChevronRight,
  Circle,
  CircleHelp,
  Clock3,
  Menu,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { AgentHome } from "./AgentHome";
import {
  DashboardSidebar,
  type DashboardSection,
} from "./DashboardSidebar";
import { ResearchLab } from "./ResearchLab";
import "./workspace.css";

const sectionCopy: Record<
  DashboardSection,
  { eyebrow: string; title: string; description: string }
> = {
  today: {
    eyebrow: "Wednesday, July 29",
    title: "Today",
    description: "See what your content team needs and what it will do next.",
  },
  pipeline: {
    eyebrow: "Content operations",
    title: "Content pipeline",
    description: "Ideas move from research to publish without getting lost.",
  },
  brand: {
    eyebrow: "Shared agent context",
    title: "Brand brain",
    description: "Give every agent the same products, voice, audience, and rules.",
  },
  research: {
    eyebrow: "Signals, not guesswork",
    title: "Research lab",
    description: "Track competitors, formats, hooks, and emerging content angles.",
  },
  calendar: {
    eyebrow: "Publishing plan",
    title: "Calendar",
    description: "Review the cadence your publishing agent will keep on track.",
  },
  analytics: {
    eyebrow: "Closed-loop learning",
    title: "Analytics",
    description: "Turn performance into the next round of creative decisions.",
  },
};

const pipelineStages = [
  {
    title: "Research",
    count: 0,
    description: "Competitor signals and viral formats.",
  },
  {
    title: "Creating",
    count: 0,
    description: "Concepts, scripts, captions and edits.",
  },
  {
    title: "Review",
    count: 0,
    description: "Your approval queue before anything ships.",
  },
  {
    title: "Scheduled",
    count: 0,
    description: "Approved posts with a publishing time.",
  },
];

const SearchDialog = ({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (section: DashboardSection) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");

  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return (Object.entries(sectionCopy) as [
      DashboardSection,
      (typeof sectionCopy)[DashboardSection],
    ][]).filter(([, item]) =>
      `${item.title} ${item.description}`.toLowerCase().includes(normalized),
    );
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  if (!open) return null;

  return (
    <div className="dashboard-search" role="presentation" onMouseDown={onClose}>
      <section
        className="dashboard-search__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Search Reelio"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dashboard-search__input">
          <Search size={18} aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search pages and actions…"
          />
          <button type="button" onClick={onClose} aria-label="Close search">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="dashboard-search__results">
          <span>Go to</span>
          {results.map(([id, item]) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                onSelect(id);
                onClose();
              }}
            >
              <span>
                <strong>{item.title}</strong>
                <small>{item.description}</small>
              </span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ))}
          {results.length === 0 ? (
            <p>No Reelio pages match “{query}”.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
};

const PipelineView = ({ onStartRun }: { onStartRun: () => void }) => (
  <section className="dashboard-pipeline">
    <div className="dashboard-pipeline__toolbar">
      <div>
        <span className="dashboard-pill">
          <Circle size={8} fill="currentColor" aria-hidden="true" />
          Nothing running yet
        </span>
        <p>Every card will show its agent, status, next action, and deadline.</p>
      </div>
      <button type="button" onClick={onStartRun}>
        <Sparkles size={15} aria-hidden="true" />
        New content run
      </button>
    </div>
    <div className="dashboard-pipeline__board">
      {pipelineStages.map((stage) => (
        <section key={stage.title} className="dashboard-pipeline__column">
          <header>
            <span>{stage.title}</span>
            <small>{stage.count}</small>
          </header>
          <div className="dashboard-pipeline__empty">
            <span>
              <Clock3 size={17} aria-hidden="true" />
            </span>
            <p>{stage.description}</p>
          </div>
        </section>
      ))}
    </div>
  </section>
);

const EmptySection = ({
  section,
  onOpenBrand,
}: {
  section: Exclude<DashboardSection, "today" | "pipeline">;
  onOpenBrand: () => void;
}) => {
  const item = sectionCopy[section];

  return (
    <section className="dashboard-empty">
      <span className="dashboard-empty__icon">
        <Sparkles size={20} aria-hidden="true" />
      </span>
      <span>Built for agent work, not another pile of menus</span>
      <h2>{item.title} comes alive after setup.</h2>
      <p>
        {item.description} This space will show the work, sources, decisions,
        and approvals behind each autonomous run.
      </p>
      <button type="button" onClick={onOpenBrand}>
        Continue setup
        <ArrowUpRight size={16} aria-hidden="true" />
      </button>
    </section>
  );
};

export const WorkspacePage = () => {
  const navigate = useNavigate();
  const { viewer, signOut } = useAuth();
  const [activeSection, setActiveSection] = useState<DashboardSection>(() => {
    const requestedSection = new URLSearchParams(window.location.search).get(
      "section",
    );
    return requestedSection && requestedSection in sectionCopy
      ? (requestedSection as DashboardSection)
      : "today";
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [focusBriefRequest, setFocusBriefRequest] = useState(0);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === "Escape") {
        setSearchOpen(false);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (!viewer) return null;

  const logout = async () => {
    await signOut();
    navigate("/auth/login", { replace: true });
  };

  const copy = sectionCopy[activeSection];
  const openNewBrief = () => {
    setActiveSection("today");
    setMobileOpen(false);
    setFocusBriefRequest((request) => request + 1);
  };

  return (
    <main className="dashboard-shell">
      <DashboardSidebar
        viewer={viewer}
        activeSection={activeSection}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        onNewBrief={openNewBrief}
        onSelect={setActiveSection}
        onSignOut={() => void logout()}
      />

      <section className="dashboard-main">
        {activeSection === "today" ? (
          <header className="dashboard-mobile-header">
            <button
              type="button"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={21} aria-hidden="true" />
            </button>
            <a href="/" aria-label="Reelio home">
              <Sparkles size={17} fill="currentColor" aria-hidden="true" />
            </a>
          </header>
        ) : (
          <header className="dashboard-header">
            <button
              type="button"
              className="dashboard-header__menu"
              aria-label="Open navigation"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={19} aria-hidden="true" />
            </button>
            <div>
              <span>{copy.eyebrow}</span>
              <h1>{copy.title}</h1>
            </div>
            <div className="dashboard-header__actions">
              <button
                type="button"
                className="dashboard-header__search"
                onClick={() => setSearchOpen(true)}
              >
                <Search size={15} aria-hidden="true" />
                Search
              </button>
              <span className="dashboard-header__status">
                <i />
                Setup needed
              </span>
            </div>
          </header>
        )}

        {activeSection === "today" ? (
          <AgentHome
            workspaceName={viewer.workspace.name}
            instagramHandle={viewer.workspace.instagramHandle}
            focusRequest={focusBriefRequest}
            onSelect={setActiveSection}
          />
        ) : activeSection === "research" ? (
          <ResearchLab />
        ) : (
          <div className="dashboard-canvas">
            <div className="dashboard-canvas__intro">
              <p>{copy.description}</p>
            </div>

            {activeSection === "pipeline" ? (
              <PipelineView onStartRun={openNewBrief} />
            ) : (
              <EmptySection
                section={activeSection}
                onOpenBrand={() => setActiveSection("brand")}
              />
            )}
          </div>
        )}
      </section>

      <SearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={setActiveSection}
      />

      <a
        className="dashboard-help-launcher"
        href="mailto:hello@reelio.app"
        aria-label="Help & feedback"
        title="Help & feedback"
      >
        <CircleHelp size={18} strokeWidth={1.8} aria-hidden="true" />
      </a>
    </main>
  );
};
